import 'react-phone-number-input/style.css';
import './PhoneInput.css';

import PhoneInputLib from 'react-phone-number-input';
import type { CountryCode } from '@minibook/shared';
import ru from 'react-phone-number-input/locale/ru.json';
import en from 'react-phone-number-input/locale/en.json';
import {
  clampPhoneInput,
  ensureRuMobileNineAfterCountryCodeIfEmpty,
  shouldBlockRuMobileLeadingNineRemoval,
  formatCountryCallingCode,
  getDefaultCountryForLanguage,
  getMaxNationalLengthForCountry,
  isPlausibleFirstNationalDigit,
  phoneValueForPicker,
  resolveCountryFromPhoneValue,
} from '@minibook/shared';
import type { CSSProperties } from 'react';
import type { FormEvent, ClipboardEvent, KeyboardEvent } from 'react';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from '../../i18n/useTranslation.js';

type Props = {
  value: string;
  onChange: (e164: string) => void;
  defaultCountry?: CountryCode;
  className?: string;
  title?: string;
  style?: CSSProperties;
  disabled?: boolean;
  invalid?: boolean;
  onBlur?: () => void;
};

export function PhoneInput({
  value,
  onChange,
  defaultCountry: defaultCountryProp,
  className,
  title,
  style,
  disabled,
  invalid,
  onBlur,
}: Props) {
  const { language } = useTranslation();
  const defaultCountry = (defaultCountryProp ?? getDefaultCountryForLanguage(language)) as CountryCode;
  const labels = useMemo(() => (language.toLowerCase().startsWith('ru') ? ru : en), [language]);
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(() =>
    resolveCountryFromPhoneValue(value || '', defaultCountry)
  );

  useEffect(() => {
    setSelectedCountry(resolveCountryFromPhoneValue(value || '', defaultCountry));
  }, [value, defaultCountry]);

  const pickerValue = useMemo(
    () => phoneValueForPicker(value || '', selectedCountry),
    [value, selectedCountry]
  );

  useLayoutEffect(() => {
    if (disabled) return;
    const next = ensureRuMobileNineAfterCountryCodeIfEmpty(value || '', selectedCountry);
    if (next !== (value || '')) onChange(next);
  }, [selectedCountry, value, disabled, onChange]);

  const countryCallingCode = formatCountryCallingCode(selectedCountry);

  const exceedsCountryLimit = (rawValue: string): boolean => {
    const digitsOnly = rawValue.replace(/\D/g, '');
    const callingCodeDigits = countryCallingCode.replace(/\D/g, '');
    const nextNational = digitsOnly.startsWith(callingCodeDigits)
      ? digitsOnly.slice(callingCodeDigits.length)
      : digitsOnly;
    if (nextNational.length > 0 && !isPlausibleFirstNationalDigit(selectedCountry, nextNational)) {
      return true;
    }
    const maxNationalLength = getMaxNationalLengthForCountry(selectedCountry, nextNational);
    return nextNational.length > maxNationalLength;
  };

  const handleBeforeInput = (event: FormEvent<HTMLInputElement>) => {
    const nativeEvent = event.nativeEvent as InputEvent;
    const input = event.currentTarget;
    const insertedText = nativeEvent.data ?? '';
    const inputType = nativeEvent.inputType ?? '';

    if (!insertedText) {
      if (
        shouldBlockRuMobileLeadingNineRemoval({
          country: selectedCountry,
          e164Value: value || '',
          formattedValue: input.value,
          selectionStart: input.selectionStart ?? 0,
          selectionEnd: input.selectionEnd ?? 0,
          inputType,
        })
      ) {
        event.preventDefault();
      }
      return;
    }

    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    const nextValue = input.value.slice(0, start) + insertedText + input.value.slice(end);
    if (exceedsCountryLimit(nextValue)) {
      event.preventDefault();
    }
  };

  const handlePhoneDigitKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Backspace' && event.key !== 'Delete') return;
    const input = event.currentTarget;
    const inputType = event.key === 'Backspace' ? 'deleteContentBackward' : 'deleteContentForward';
    if (
      shouldBlockRuMobileLeadingNineRemoval({
        country: selectedCountry,
        e164Value: value || '',
        formattedValue: input.value,
        selectionStart: input.selectionStart ?? 0,
        selectionEnd: input.selectionEnd ?? 0,
        inputType,
      })
    ) {
      event.preventDefault();
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const pastedText = event.clipboardData.getData('text');
    if (!pastedText) return;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    const nextValue = input.value.slice(0, start) + pastedText + input.value.slice(end);
    if (exceedsCountryLimit(nextValue)) {
      event.preventDefault();
      const clamped = clampPhoneInput(nextValue, selectedCountry);
      if (clamped !== (value || '')) onChange(clamped);
    }
  };

  const kitClass = ['PhoneInputKit', invalid ? 'PhoneInputKit--error' : '', disabled ? 'PhoneInputKit--disabled' : '', className || '']
    .filter(Boolean)
    .join(' ');

  return (
    <div className={kitClass} style={style} title={title}>
      <PhoneInputLib
        international
        countryCallingCodeEditable={false}
        country={selectedCountry}
        defaultCountry={defaultCountry}
        labels={labels}
        value={pickerValue || undefined}
        onCountryChange={(nextCountry) => {
          if (!nextCountry) return;
          const next = nextCountry as CountryCode;
          setSelectedCountry(next);
          let nextVal = clampPhoneInput(typeof pickerValue === 'string' ? pickerValue : '', next);
          nextVal = ensureRuMobileNineAfterCountryCodeIfEmpty(nextVal, next);
          if (nextVal !== (value || '')) onChange(nextVal);
        }}
        onChange={(v) => onChange(clampPhoneInput(typeof v === 'string' ? v : '', selectedCountry))}
        disabled={disabled}
        className="PhoneInput"
        numberInputProps={{
          autoComplete: 'tel-national',
          inputMode: 'numeric',
          onBeforeInput: handleBeforeInput,
          onKeyDown: handlePhoneDigitKeyDown,
          onPaste: handlePaste,
        }}
        onBlur={() => {
          onBlur?.();
        }}
      />
    </div>
  );
}
